import React from 'react';
import round from 'lodash/round';
import max from 'lodash/max';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import GeneralChart from '@/components/seo/GeneralChart';
import BarYAxisLabel from './BarYAxisLabel';
import { JobTitleContext } from '@/context/create-context';
import { SCALE_BAR_TIP, EDUCATION_MAP } from '@/constants/job-title';
import { CoreDimensionDataType } from '@/types/job-title';
import { numberRound } from '@/utils/numbers';

const SalaryEducation = (props: {
  id: string;
  educationData: Record<string, any>;
}) => {
  const { id, educationData } = props;

  const {
    jobTitle: {
      salary_details: {
        avg_value_by_education_details: avgValueByEducationDetails,
      },
    },
  } = React.useContext(JobTitleContext);
  const barChartRef = React.useRef(null);

  const [educationChartData, educationBarTipPercentage] = React.useMemo<
    [CoreDimensionDataType, Record<string, number>]
  >(() => {
    const data: CoreDimensionDataType = [];
    const educationBarTipPercentage: { [key: string]: number } = {};
    const educationMax = max(
      Object.values(avgValueByEducationDetails).map((item: any) => item['max']),
    );
    Object.keys(EDUCATION_MAP).forEach((key) => {
      const salaryRange = Reflect.get(avgValueByEducationDetails, key);
      if (!salaryRange) return;
      data.push({
        label: EDUCATION_MAP[key],
        range: [salaryRange.min, salaryRange.max],
      });
      const percentage =
        (SCALE_BAR_TIP * educationMax) / (salaryRange.max - salaryRange.min);
      educationBarTipPercentage[EDUCATION_MAP[key]] =
        percentage >= 1 ? 1 : percentage <= 0 ? 0 : percentage;
    });
    return [data, educationBarTipPercentage];
  }, [avgValueByEducationDetails]);

  const barConfig = React.useMemo<ECOption>(() => {
    const data = [...educationChartData];
    const reverseData = data.reverse();
    const chartLowData = reverseData.map(({ label, range }) => ({
      name: label,
      value: range[0],
    }));
    const chartHighData = reverseData.map(({ label, range }) => ({
      name: label,
      value: range[1] - range[0],
    }));

    return {
      grid: {
        show: false,
        containLabel: true,
        top: 0,
        bottom: 0,
        left: 25,
        right: 30,
      },
      xAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#6e6e6e',
          fontSize: 14,
          formatter: (item: number) => numberRound(item),
          margin: 15,
        },
        splitLine: { show: false },
        splitNumber: 4,
      },
      yAxis: {
        interval: true,
        type: 'category',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: true, lineStyle: { width: 1, color: '#f0f0f0' } },
      },
      tooltip: {
        confine: true,
        padding: 10,
        borderColor: '#f0f0f0',
        borderWidth: 1,
        backgroundColor: '#fff',
        shadowColor: 'transparent',
        textStyle: { fontSize: 14, fontWeight: 400, color: '#2d2d2d' },
        formatter: (params) => {
          const name = 'name' in params ? params.name : '';
          const lowValue =
            chartLowData.find((item) => item.name === name)?.value || 0;
          const highValue =
            chartHighData.find((item) => item.name === name)?.value || 0;
          return `<p style="font-size: 12px">${name}</p><p style="margin-top: 4px;">平均薪资: <span style="color:#0050b3;margin-left: 4px">¥${round(
            (lowValue * 2 + highValue) / 2,
            -2,
          )}</span></p>`;
        },
      },
      series: [
        {
          type: 'bar',
          data: chartLowData,
          emphasis: { disabled: true },
          cursor: 'default',
          barWidth: 30,
          itemStyle: {
            borderWidth: 0,
            color: '#fff',
          },
          stack: 'total',
        },
        {
          type: 'bar',
          data: chartHighData,
          barWidth: 30,
          emphasis: { disabled: true },
          cursor: 'default',
          stack: 'total',
          label: {
            show: true,
            position: 'insideLeft',
            color: '#2d2d2d',
            formatter: ({ dataIndex, value }) => {
              const lowData = chartLowData[dataIndex].value;
              return `${numberRound(lowData)}-${numberRound(
                lowData + Number(value),
              )}`;
            },
          },
          itemStyle: {
            borderWidth: 0,
            borderColor: 'transparent',
            shadowColor: 'transparent',
            borderRadius: 1,
            color: ({ name }) => {
              const percentage = educationBarTipPercentage[name];
              return {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 1,
                y2: 0,
                colorStops: [
                  {
                    offset: 0,
                    color: '#0050b3',
                  },
                  {
                    offset: percentage,
                    color: '#0050b3',
                  },
                  {
                    offset: percentage,
                    color: '#f8f9fe',
                  },
                  {
                    offset: 1,
                    color: '#f8f9fe',
                  },
                ],
              };
            },
          },
        },
      ],
    };
  }, [educationChartData, educationBarTipPercentage]);

  return (
    <GeneralChart
      id={id}
      title="影响薪资的核心维度2：学历背景"
      tip={educationData['tip']}
    >
      <p className="mb-2">{educationData['一句话总结'] || ''}</p>
      <div className="flex my-4">
        <BarYAxisLabel chartData={educationChartData} />
        <div className="w-full grow [&_canvas]:cursor-default">
          <CusEcharts chartRef={barChartRef} config={barConfig} />
        </div>
      </div>
      <p className="font-semibold mb-2">影响因素</p>
      <ul className="list-inside !ml-2 mb-2">
        {(educationData?.['影响因素'] || '' || []).map(
          (item: string, index: number) => (
            <li key={index}>{item}</li>
          ),
        )}
      </ul>
    </GeneralChart>
  );
};

export default SalaryEducation;
